import { useEffect, useState } from "react";

import {
  buildPath,
  buildTemplatePath,
  Button,
  ListContainer,
  ListContext,
  Page,
  
  PageRoute,
  pn,
  SearchBar,
  Stack,
  Typography,
} from "@compass/core";

import { ListFooter } from "components";
import {
  NonPersonnelFilter as NonPersonnelFilterModel,
  NonPersonnelListDto,
  NonPersonnelListFilter,
  NonPersonnelListResource,
  Permission,
  PermissionAction,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import { useDispatch } from "react-redux";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { nonPersonnelPageRoute } from "../NonPersonnelPage/NonPersonnelPage";
import { NonPersonnelFilter } from "../NonPersonnelFilter";
import { NonPersonnelTable } from "./NonPersonnelTable";
import produce from "immer";

const routeName = "nonPersonnels";

export const nonPersonnelListPageRoute: PageRoute = {
  handle: "Non-Personnel",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const NonPersonnelListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.NonPersonnel;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [listItems, setListItems] = useState<NonPersonnelListDto[]>([]);
  const { listFilter, setListFilter } = useInitListFilter<NonPersonnelListFilter>({
    sortObjects: [{ property: pn<NonPersonnelListDto>("date"), isDesc: true }],
  });
  const [initListFilter] = useState<NonPersonnelListFilter>(listFilter);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<NonPersonnelListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const { tableContainerRef, applyPage, applyPageSize, applySortMethod, applySort, applySearch, loadList } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.nonPersonnels.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.nonPersonnels.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);
    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.nonPersonnels.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<NonPersonnelListFilter>({
    maxWidth: "md",
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <NonPersonnelFilter
        filter={listFilter.nonPersonnelFilter || ({} as NonPersonnelFilterModel)}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.nonPersonnelFilter = filter;
            })
          )
        }
      />
    ),
  });

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="inherit">{nonPersonnelListPageRoute.handle}</Typography>

            {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
              <Button
                onClick={() => navigate(nonPersonnelPageRoute.getPath({ id: 0, crudMode: "edit" }))}
                variant="outlined"
              >
                + New
              </Button>
            )}
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <NonPersonnelTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              nonPersonnels={listItems}
              resource={resource}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
